import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import {
    Aangever,
    Aangifte, BokkzAlgDataService, IncomeTaxEntity, OmschrijvingEnBedrag,
    PGASpecifiekeZorgkosten,
    ReiskostenEigenZorg
} from '@snelstart-ventures/fiscaal/domain-logic';
import { FormArray, FormBuilder, FormGroup } from '@angular/forms';
import { BokkzIbDataService } from '@snelstart-ventures/fiscaal/domain-logic';
import { SPINNER_TIMEOUT } from '../../../gedeelde-componenten/gedeelde-componenten.component';
import { filter, Subscription } from 'rxjs';
import { Event, NavigationEnd, Router } from '@angular/router';
import { SharedUiFormControlsModule } from '@snelstart/ui-form-controls';
import { SharedUiButtonModule } from '@snelstart/ui-button';
import { SharedUiDetailsModule } from '@snelstart/ui-details';
import { CommonModule } from '@angular/common';


@Component({
    selector: 'snelstart-venture-fiscaal-feature-reiskosten-eigen-zorg',
    templateUrl: './reiskosten-eigen-zorg.component.html',
    styleUrls: ['./reiskosten-eigen-zorg.component.scss'],
    imports: [
        SharedUiFormControlsModule,
        SharedUiButtonModule,
        SharedUiDetailsModule,
        CommonModule
    ]
})
export class ReiskostenEigenZorgComponent implements OnInit, OnDestroy {

    private routerSubscription: Subscription | undefined;
    private incomeTaxEntitySubscription: Subscription | undefined;
    private clientEntitySubscription: Subscription | undefined;
    private isSaving = false;
    @Input() public id!: string;
    public clientId!: string;
    aangifte = {} as Aangifte;
    public incomeTaxEntity = {} as IncomeTaxEntity;
    private focusedElementId: string | null = null;

    public reiskostenEigenZorg = {} as FormGroup;

    constructor(public dataService: BokkzIbDataService, public dataAlgService: BokkzAlgDataService, private router: Router, private fb: FormBuilder) {
        this.reiskostenEigenZorg = this.fb.group({
            pgaReiskostenEigenZorg:
                this.fb.array<PGASpecifiekeZorgkosten>([])
        });
    }

    get pgaReiskostenEigenZorg(): FormArray {
        return this.reiskostenEigenZorg.get('pgaReiskostenEigenZorg') as FormArray;
    }

    addRijReiskostenEigenZorg(): void {
        this.pgaReiskostenEigenZorg.push(this.createReiskostenEigenZorgFormGroup()
        );
    }

    verwijderRijRieskostenEigenZorg(index: number): void {
        this.pgaReiskostenEigenZorg.removeAt(index);
        this.reiskostenEigenZorg.updateValueAndValidity();
        this.reiskostenEigenZorg.markAsDirty();
        this.saveForm();
    }


    createReiskostenEigenZorgFormGroup(reiskosten?: ReiskostenEigenZorg): FormGroup {
        const prijsPerKm = this.aangifte.aangifteJaar > 2024
            ? 23
            : (reiskosten ? reiskosten.prijsPerKilometerInCenten : undefined);

        return this.fb.group(({
            extraReisKostenZiekteInvaliditeit: reiskosten ? reiskosten.extraReisKostenZiekteInvaliditeit : undefined,
            uitgavenOpenbaarVervoerEnOverigVervoer: reiskosten ? reiskosten.uitgavenOpenbaarVervoerEnOverigVervoer : undefined,
            aantalKilometersNaartArtsZiekenhuis: reiskosten ? reiskosten.aantalKilometersNaartArtsZiekenhuis : undefined,
            prijsPerKilometerInCenten: prijsPerKm
        } as ReiskostenEigenZorg));
    }

    public ngOnInit(): void {
        this.incomeTaxEntitySubscription = this.dataService.incomeTaxEntity.subscribe((incomeTaxEntity) => {
            this.incomeTaxEntity = incomeTaxEntity;
            if (incomeTaxEntity.aangifte) {
                const aangifte = incomeTaxEntity.aangifte;
                this.aangifte = aangifte;
                this.updateForm();
            }
        });
        this.clientEntitySubscription = this.dataAlgService.client.subscribe((client) => {
            this.clientId = client.sstRelationId!;
        });
        this.routerSubscription = this.router.events.pipe(filter((event: Event): event is NavigationEnd => event instanceof NavigationEnd))
            .subscribe((event: NavigationEnd) => {
                this.handleUrlChange(event.urlAfterRedirects);
            });
    }

    ngOnDestroy(): void {
        if (this.routerSubscription) {
            this.routerSubscription.unsubscribe(); // Unsubscribe to avoid memory leaks
        }
        if (this.incomeTaxEntitySubscription) {
            this.incomeTaxEntitySubscription.unsubscribe();
        }
        if (this.clientEntitySubscription) {
            this.clientEntitySubscription.unsubscribe();
        }
    }

    handleUrlChange(newUrl: string) {
        if (!this.isSaving && this.hasUnsavedChanges()) {
            this.isSaving = true;
            this.dataService.postIncomeTaxEntity(this.clientId, false, this.incomeTaxEntity);
            this.isSaving = false;
        }
    }

    private hasUnsavedChanges(): boolean {
        return this.reiskostenEigenZorg.dirty;
    }

    private updateForm(): void {
        const reiskostenLijst = this.aangifte?.persoonsgebondenAftrek?.pgaSpecifiekeZorgkosten?.reiskostenEigenZorg || [];
        const formArray = this.reiskostenEigenZorg.get('pgaReiskostenEigenZorg') as FormArray;
        const focusedControl = this.focusedElementId ? document.getElementById(this.focusedElementId) as HTMLElement : null;

        while (formArray.length > reiskostenLijst.length) {
            formArray.removeAt(formArray.length - 1);
        }

        // Add or update controls
        for (let i = 0; i < reiskostenLijst.length; i++) {
            if (i < formArray.length) {
                // Update existing control
                const control = formArray.at(i) as FormGroup;
                control.patchValue(reiskostenLijst[i]);
            } else {
                // Add new control
                const formGroup = this.createReiskostenEigenZorgFormGroup(reiskostenLijst[i]);
                formArray.push(formGroup);
            }
        }

        if (focusedControl && document.contains(focusedControl)) {
            focusedControl.focus();
        }
    }

    saveForm(): void {
        const formValues = this.reiskostenEigenZorg.value.pgaReiskostenEigenZorg as ReiskostenEigenZorg[];

        if (this.aangifte.aangifteJaar > 2024) {
            formValues.forEach(r => {
                if (r.aantalKilometersNaartArtsZiekenhuis != undefined && r.aantalKilometersNaartArtsZiekenhuis > 0) {
                    r.prijsPerKilometerInCenten = 23;
                }
            });
        }

        this.incomeTaxEntity.aangifte = {
            ...this.aangifte,
            persoonsgebondenAftrek: {
                ...this.aangifte.persoonsgebondenAftrek,
                pgaSpecifiekeZorgkosten: {
                    ...this.aangifte.persoonsgebondenAftrek?.pgaSpecifiekeZorgkosten,
                    reiskostenEigenZorg: formValues
                }
            }
        } as Aangifte;
        this.dataService.updateIncomeTaxEntity(this.incomeTaxEntity);
    }
}
